<template>
  <article>
    <p>
      Of the many factors that determine the characteristics of a given wine,
      the grape variety is perhaps the most important. Although there are over
      10,000 different documented varietals within the species Vitis Vinifera
      (regarded as the "true" wine grapes), the grapes listed on this page are
      the ones most commonly cultivated for fine wines.
    </p>
    <p>
      Each grape varietal has differing aroma and flavor characteristics when it
      is made into wine. But wines made from individual varietals will also vary
      in character depending on where in the world the grapes are grown: for
      example, vineyards in New World growing regions will produce wines with
      different flavors and aromas than vineyards from Old World growing
      regions, even when the wines are made from the exact same grape varietal.
      Grapes from France's Rhône Valley, for example, will typically not taste
      the same as Italy's Piedmont wine region. Thus, Italian wine will have a
      distinct taste that differentiates it from French wine. Halfway across the
      world in North America, California wine (such as the grapes used to make
      Napa valley wine) will have a completely different and distinctive taste.
    </p>
    <p>
      There are various types of grapes that are used to make our favorite
      wines. Such as the Sauvignon blanc grape, a green-skinned grape that hails
      from the Bourdeaux region of France. Another example is the chardonnay
      grape, used to make one of the most recognized and glamorous wines in the
      world. Other grape varieties include the pinot gris (white varietal) the
      ugni blanc (one of the most widely planted grape varieties in the world),
      the petit verdot (a variety of red wine grape), the pinot meunier (a
      variety of black wine grape used for making Champagne), amongst others.
    </p>
    <p>
      It would behoove all wine lovers to learn about the various types of
      grapes that make up their varietal wines. From dry whites to sparkling
      wines to dessert wines, grape types play a vital role.
    </p>
    <p>
      Use the links below to find top-rated wines for each of the varietals and
      learn about what makes each varietal unique.
    </p>
    <div class="varietals">
      <div class="varietals-column red text-center">
        <ul class="nobullet" itemscope itemtype="http://schema.org/ItemList">
          <li itemprop="name"><h2>Red Grapes</h2></li>
          <link
            itemprop="itemListOrder"
            href="http://schema.org/ItemListOrderAscending"
          />
          <li
            v-for="item in red_varietals"
            :key="item.slug"
            itemprop="itemListElement"
          >
            <nuxt-link :to="`/store/varietals/${item.slug}`">{{
              item.name
            }}</nuxt-link>
          </li>
        </ul>
      </div>
      <div class="varietals-column white text-center">
        <ul class="nobullet" itemscope itemtype="http://schema.org/ItemList">
          <li itemprop="name"><h2>White Grapes</h2></li>
          <link
            itemprop="itemListOrder"
            href="http://schema.org/ItemListOrderAscending"
          />
          <li
            v-for="item in white_varietals"
            :key="item.slug"
            itemprop="itemListElement"
          >
            <nuxt-link :to="`/store/varietals/${item.slug}`">{{
              item.name
            }}</nuxt-link>
          </li>
        </ul>
      </div>
    </div>
  </article>
</template>

<script>
export default {
  data() {
    return {
      red_varietals: [{ slug: 'red-wine', display_name: 'Red Wine' }],
      white_varietals: [{ slug: 'white-wine', display_name: 'White Wine' }],
    }
  },
  async fetch() {
    const result = await this.$axios.get('/api/learn/varietals')
    this.red_varietals = result.data.red_varietals
    this.white_varietals = result.data.white_varietals
  },
  head: {
    meta: [
      {
        hid: 'description',
        name: 'description',
        content:
          'Across the wine world, different regions are known for producing different grapes and subsequently different styles of wine.',
      },
    ],
  },
}
</script>

<style scoped lang="scss">
.varietals {
  display: grid;
  max-width: 1000px;
  margin: 0 auto;
  grid-template-areas: 'red white';
  justify-content: stretch;

  .varietals-column {
    &:first-child {
      border-right: 1px solid #c9c9c9;
    }

    &.red {
      grid-area: red;
    }
    &.white {
      grid-area: white;
    }

    ul li {
      font-size: 1.25rem;
    }
  }
}
</style>
